import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { useState } from 'react';

import { MemberPurchaseOrderItem } from '../models/member-purchase-order';
import { SHOP_CART_CHANGED } from '../../../common/components/shop-link';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

type CartLine = Pick<MemberPurchaseOrderItem, 'productId' | 'quantity' | 'isAvailableForPurchase'>;

/**
 * Lays articles into the shop's cart and says so, without leaving the portal
 * (#2279, #2282). Articles that are no longer sold are skipped.
 */
export const useAddToShopCart = (): { adding: boolean; add: (lines: CartLine[]) => Promise<void> } => {
    const dispatch = useThunkDispatch();
    const [adding, setAdding] = useState(false);

    const add = async (lines: CartLine[]): Promise<void> => {
        const buyable = lines.filter(line => null !== line.productId && line.isAvailableForPurchase);

        setAdding(true);

        await dispatch(async (_dispatch, _getState, { api }) => {
            let added = 0;

            // One after the other: the cart is one record, and parallel writes
            // to it lose lines.
            for (const line of buyable) {
                const result = await api.orders.addToCart(line.productId as number, line.quantity);
                added += result.success ? 1 : 0;
            }

            if (added > 0) {
                window.dispatchEvent(new Event(SHOP_CART_CHANGED));
            }

            dispatch(
                Snackbar.enqueue(
                    added > 0
                        ? Snackbar.PoolNotification.success('app.orders.addedToCart', {
                              count: added,
                              skipped: lines.length - added,
                          })
                        : Snackbar.PoolNotification.error('app.orders.addedToCart.none')
                )
            );
        });

        setAdding(false);
    };

    return { adding, add };
};
